import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { acquireNexusApiAccessTokenAsync } from '../../../../services/auth/AuthService';
import { useProviderServices } from '../../../../app/providers/AppServicesContext';
import { alerts } from '../../../../shared/ui/Alerts';
import { FunctionToolDefinition } from '../../../../domain/agents/models/FunctionTool';
import { NexusMcpServerDto } from '../../../../services/providers/nexus/dtos/responses/NexusMcpServerDto';


export interface CreateAgentSubmitRequest {
    name: string;
    instructions: string;
    description?: string;
    agentIconFile?: File;
    model?: string;
    functionTools?: FunctionToolDefinition[];
    filesForFileSearch?: File[];
    filesForCodeInterpreter?: File[];
    enableCodeInterpreter?: boolean;
    enableFileSearch?: boolean;
    mcpServerIds?: string[];
    metadata?: Record<string, unknown>;
}

export interface UseCreateAgentPageVm {
    availableModels: string[];
    defaultModel: string;
    maxFileCount: number;
    maxFileSizeMb: number;
    availableMcpServers: NexusMcpServerDto[];
    isMcpServersLoading: boolean;
    onCancel: () => void;
    onSubmit: (request: CreateAgentSubmitRequest) => Promise<void>;
}

const parseModels = (): string[] => {
    try {
        const raw = JSON.parse(process.env.REACT_APP_NEXUS_MODELS || '[]');
        return Array.isArray(raw) ? raw.filter((model) => typeof model === 'string' && model.length > 0) : [];
    } catch {
        return [];
    }
};

export const useCreateAgentPage = (): UseCreateAgentPageVm => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const providerServices = useProviderServices();

    const availableModels = useMemo(() => parseModels(), []);
    const defaultModel = availableModels[0] ?? 'gpt-5.4-mini';
    const maxFileCount = Number(process.env.REACT_APP_MAX_FILE_COUNT_UPLOAD_AGENT_LEVEL || '100');
    const maxFileSizeMb = Number(process.env.REACT_APP_MAX_FILE_SIZE_UPLOAD_AGENT_LEVEL || '30');

    const [availableMcpServers, setAvailableMcpServers] = useState<NexusMcpServerDto[]>([]);
    const [isMcpServersLoading, setIsMcpServersLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetchMcpServers = async () => {
            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({ msalInstance: instance });
                const servers = await providerServices.nexus.mcpServers.getMcpServersAsync(accessToken);
                if (!cancelled) {
                    setAvailableMcpServers(servers);
                }
            } catch {
                // silently fall back to empty list
            } finally {
                if (!cancelled) {
                    setIsMcpServersLoading(false);
                }
            }
        };

        fetchMcpServers();

        return () => {
            cancelled = true;
        };
    }, [instance, providerServices]);

    const onCancel = useCallback(() => {
        navigate('/manage-agents');
    }, [navigate]);

    const onSubmit = useCallback(
        async (request: CreateAgentSubmitRequest): Promise<void> => {
            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                await providerServices.nexus.agents.createAgentAsync(request, accessToken);
                alerts.success('Agent created successfully.');
                navigate('/manage-agents');
            } catch {
                alerts.error('Failed to create agent. Please try again.');
            }
        },
        [instance, navigate, providerServices],
    );

    return {
        availableModels,
        defaultModel,
        maxFileCount,
        maxFileSizeMb,
        availableMcpServers,
        isMcpServersLoading,
        onCancel,
        onSubmit,
    };
};

export default useCreateAgentPage;
